'use client'

import type { PropsWithChildren } from 'react'
import { useEffect } from 'react'
// eslint-disable-next-line no-restricted-imports
import type {
  FieldErrors,
  FieldError as RHFFieldError,
  FieldValues as RHFFieldValues,
  UseFormReturn as RHFUseFormReturn,
  UseFormProps,
} from 'react-hook-form'
// eslint-disable-next-line no-restricted-imports
import {
  FormProvider,
  useFormContext,
  useFormState,
  useForm as useVendorUseForm,
  useWatch,
} from 'react-hook-form'

// FIXME: Reminder that validating multiple async fields is not performed concurrently but
// one after the other. Related issue:
// https://github.com/orgs/react-hook-form/discussions/5931

/**
 * Scrolls to the first field with an error when the form is submitted.
 */
function ScrollToFieldError() {
  const {
    formState: { errors, isSubmitting },
  } = useFormContext()

  useEffect(() => {
    const ref = document.querySelector('[data-invalid=true]')

    if (!(ref instanceof HTMLElement)) {
      return
    }

    ref.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    })
    ref.focus({ preventScroll: true })
  }, [errors, isSubmitting])

  return null
}

export type FieldValues = RHFFieldValues
export type FieldError = RHFFieldError
export type UseFormReturn<T extends FieldValues> = RHFUseFormReturn<T>
export { FormProvider, useFormContext, useFormState, useWatch }

// -- Form

export type FormProps<T extends FieldValues> = PropsWithChildren<{
  form?: UseFormReturn<T>
  onSubmitAction: (data: T) => void
  onSubmitError?: (errors: FieldErrors<T>) => void
  className?: string
}>

export function useForm<T extends FieldValues>(
  options: StrictOmit<UseFormProps<T>, 'mode' | 'shouldUseNativeValidation' | 'shouldFocusError'>,
) {
  return useVendorUseForm<T>({
    ...options,
    mode: 'onSubmit',
    shouldUseNativeValidation: false,
    shouldFocusError: false,
  })
}

export function Form<T extends FieldValues>({
  children,
  form: givenForm,
  onSubmitAction: onSubmit,
  onSubmitError,
  className,
}: FormProps<T>) {
  const defaultForm = useForm<T>({})
  const form = givenForm ?? defaultForm

  return (
    <FormProvider {...form}>
      <ScrollToFieldError />
      <form noValidate onSubmit={form.handleSubmit(onSubmit, onSubmitError)} className={className}>
        {children}
      </form>
    </FormProvider>
  )
}
